import React, { useState } from "react";
import { PageHeader } from "@/components/Layout";
import { api, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";
import { ChartBar, DownloadSimple, Play } from "@phosphor-icons/react";

const REPORTS = [
  { key: "revenue_by_client", label: "Revenue by Client", money: true },
  { key: "revenue_by_lead_source", label: "Revenue by Lead Source", money: true },
  { key: "revenue_by_tag", label: "Revenue by Tag", money: true },
  { key: "revenue_by_service", label: "Revenue by Service", money: true },
  { key: "lead_sources_count", label: "Lead Sources (client count)", money: false },
  { key: "jobs_summary", label: "Jobs Summary", money: false },
];

const PRESETS = [
  { key: "7", label: "Last 7 days" },
  { key: "30", label: "Last 30 days" },
  { key: "90", label: "Last 3 months" },
  { key: "180", label: "Last 6 months" },
  { key: "365", label: "Last year" },
  { key: "custom", label: "Custom range" },
];

const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);
const BAR_COLORS = ["#00B4D8", "#0B1726", "#10B981", "#F59E0B", "#6366F1", "#F43F5E", "#8B5CF6", "#0EA5E9", "#14B8A6", "#EC4899"];

export default function Reports() {
  const [type, setType] = useState("revenue_by_client");
  const [preset, setPreset] = useState("365");
  const [start, setStart] = useState(isoDaysAgo(365));
  const [end, setEnd] = useState(today());
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const meta = REPORTS.find((r) => r.key === type);

  const onPreset = (p) => {
    setPreset(p);
    if (p !== "custom") { setStart(isoDaysAgo(Number(p))); setEnd(today()); }
  };

  const run = async () => {
    setLoading(true);
    try {
      const data = await api.runReport({ type, start, end });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Report failed"); }
    finally { setLoading(false); }
  };

  const fmt = (v, col) => (meta?.money && col > 0 ? money(v) : v);

  const exportCsv = () => {
    if (!result?.rows?.length) return;
    const lines = [result.columns.join(",")];
    result.rows.forEach((r) => lines.push(r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${type}_${start}_to_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]">
      <PageHeader title="Reports" subtitle="Pull performance reports on demand" />

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <Label>Report</Label>
            <Select value={type} onValueChange={(v) => { setType(v); setResult(null); }}>
              <SelectTrigger className="mt-1.5" data-testid="report-type"><SelectValue /></SelectTrigger>
              <SelectContent>{REPORTS.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Date range</Label>
            <Select value={preset} onValueChange={onPreset}>
              <SelectTrigger className="mt-1.5" data-testid="report-range"><SelectValue /></SelectTrigger>
              <SelectContent>{PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>From</Label><Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPreset("custom"); }} className="mt-1.5" data-testid="report-start" /></div>
          <div><Label>To</Label><Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPreset("custom"); }} className="mt-1.5" data-testid="report-end" /></div>
        </div>
        <div className="mt-4">
          <Button onClick={run} disabled={loading} className="bg-[#00B4D8] hover:bg-cyan-600" data-testid="run-report">
            <Play size={16} weight="fill" className="mr-1.5" /> {loading ? "Running…" : "Run Report"}
          </Button>
        </div>
      </div>

      {result && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="report-result">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ChartBar size={18} weight="duotone" className="text-[#00B4D8]" />
              <h3 className="font-display font-bold text-slate-900">{meta?.label}</h3>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500">Total: <b className="text-slate-900">{meta?.money ? money(result.total) : result.total}</b></span>
              <Button variant="outline" size="sm" onClick={exportCsv} disabled={!result.rows.length} data-testid="report-export"><DownloadSimple size={16} className="mr-1.5" /> Export CSV</Button>
            </div>
          </div>

          {result.rows.length === 0 ? (
            <p className="text-slate-400 text-sm py-10 text-center">No data for this period.</p>
          ) : (
            <>
              {result.chart?.length > 0 && (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={result.chart} margin={{ left: -10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748B" }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} tickFormatter={(v) => (meta?.money ? `$${v}` : v)} />
                    <Tooltip formatter={(v) => (meta?.money ? money(v) : v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {result.chart.map((e, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              <div className="mt-5 border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    {result.columns.map((c, i) => <TableHead key={c} className={i > 0 ? "text-right" : ""}>{c}</TableHead>)}
                  </TableRow></TableHeader>
                  <TableBody>
                    {result.rows.map((row, ri) => (
                      <TableRow key={ri} data-testid={`report-row-${ri}`}>
                        {row.map((cell, ci) => <TableCell key={ci} className={ci > 0 ? "text-right font-semibold" : "font-medium text-slate-800"}>{fmt(cell, ci)}</TableCell>)}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
